import { ENABLED_MODULES, type UserModulesList } from '@/constants/modules';
import type { DichotomousState } from '@/features/labelize/dichotomous/dichotomousSearch';

const STORAGE_KEYS = {
  ACCESS_TOKEN: 'accessToken',
  USER_MAIL: 'userMail',
  MODULES: 'modules',
  REMEMBER_ME: 'rememberMe',
  ROLE: 'userRole',
  COMPANY: 'userCompany',
  THEME: 'theme',
  LANGUAGE: 'toollife_language',
  SIDEBAR_COLLAPSED: 'sidebarCollapsed',
  LABELING_AUTO_ADVANCE: 'labelingAutoAdvance',
};

export class StorageService {
  constructor() {}

  private get storage() {
    return this.getRememberMe() ? localStorage : sessionStorage;
  }

  getAccessToken(): string | null {
    return this.storage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  }

  setAccessToken(token: string): void {
    this.storage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
  }

  getRole(): string | null {
    return this.storage.getItem(STORAGE_KEYS.ROLE);
  }

  setRole(role: string): void {
    this.storage.setItem(STORAGE_KEYS.ROLE, role);
  }

  removeRole(): void {
    this.storage.removeItem(STORAGE_KEYS.ROLE);
  }

  getCompany(): string | null {
    return this.storage.getItem(STORAGE_KEYS.COMPANY);
  }

  setCompany(company: string): void {
    this.storage.setItem(STORAGE_KEYS.COMPANY, company);
  }

  removeCompany(): void {
    this.storage.removeItem(STORAGE_KEYS.COMPANY);
  }

  removeAccessToken(): void {
    this.storage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  }

  getUserMail(): string | null {
    return this.storage.getItem(STORAGE_KEYS.USER_MAIL);
  }

  setUserMail(username: string): void {
    this.storage.setItem(STORAGE_KEYS.USER_MAIL, username);
  }

  setModules(modules: string[]): void {
    this.storage.setItem(STORAGE_KEYS.MODULES, JSON.stringify(modules));
  }

  getModules(): UserModulesList {
    const raw = this.storage.getItem(STORAGE_KEYS.MODULES);
    try {
      const storedModules = raw ? (JSON.parse(raw) as UserModulesList) : [];
      return storedModules.filter((m) => ENABLED_MODULES.includes(m));
    } catch {
      return [];
    }
  }

  removeModules(): void {
    this.storage.removeItem(STORAGE_KEYS.MODULES);
  }

  setRememberMe(value: boolean): void {
    localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, value ? 'true' : 'false');
  }

  getRememberMe(): boolean {
    return localStorage.getItem(STORAGE_KEYS.REMEMBER_ME) === 'true';
  }

  getTheme(): string | null {
    return localStorage.getItem(STORAGE_KEYS.THEME);
  }

  setTheme(theme: string): void {
    localStorage.setItem(STORAGE_KEYS.THEME, theme);
  }

  getLanguage(): string | null {
    return localStorage.getItem(STORAGE_KEYS.LANGUAGE);
  }

  setLanguage(lang: string): void {
    localStorage.setItem(STORAGE_KEYS.LANGUAGE, lang);
  }

  getChartConfig(key: string): string | null {
    return localStorage.getItem(key);
  }

  setChartConfig(key: string, value: string): void {
    localStorage.setItem(key, value);
  }

  removeChartConfig(key: string): void {
    localStorage.removeItem(key);
  }

  /**
   * Free-form UI preferences (last selected reference, visible panels…). Always in
   * `localStorage`: they must outlive the session whatever "remember me" says.
   */
  getPreference(key: string): string | null {
    return localStorage.getItem(key);
  }

  setPreference(key: string, value: string): void {
    localStorage.setItem(key, value);
  }

  removePreference(key: string): void {
    localStorage.removeItem(key);
  }

  getSidebarCollapsed(): boolean {
    return localStorage.getItem(STORAGE_KEYS.SIDEBAR_COLLAPSED) === 'true';
  }

  setSidebarCollapsed(value: boolean): void {
    localStorage.setItem(STORAGE_KEYS.SIDEBAR_COLLAPSED, value ? 'true' : 'false');
  }

  getLabelingDichotomousBySession(sessionId: string): { state: DichotomousState } | null {
    try {
      const raw = localStorage.getItem(`labeling:${sessionId}:dichotomous`);
      return raw ? (JSON.parse(raw) as { state: DichotomousState }) : null;
    } catch {
      return null;
    }
  }

  setLabelingDichotomousBySession(sessionId: string, value: { state: DichotomousState }): void {
    localStorage.setItem(`labeling:${sessionId}:dichotomous`, JSON.stringify(value));
  }

  removeLabelingDichotomousBySession(sessionId: string): void {
    localStorage.removeItem(`labeling:${sessionId}:dichotomous`);
  }

  getLabelingAutoAdvance(): boolean {
    return localStorage.getItem(STORAGE_KEYS.LABELING_AUTO_ADVANCE) !== 'false';
  }

  setLabelingAutoAdvance(value: boolean): void {
    localStorage.setItem(STORAGE_KEYS.LABELING_AUTO_ADVANCE, value ? 'true' : 'false');
  }

  clear(): void {
    sessionStorage.clear();
    if (!this.getRememberMe()) {
      localStorage.clear();
    } else {
      this.removeAccessToken();
      this.removeModules();
      this.removeRole();
      this.removeCompany();
    }
  }
}
